import { useEffect, useState, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { InvoiceAPI, SettingsAPI, ReceiptAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { todayISO, addDaysISO, STATUS_OPTIONS, formatCurrency } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Trash, FloppyDisk, ArrowLeft, Paperclip, X, FilePdf, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const blankItem = () => ({ description: "", quantity: 1, unit_price: 0, miles: 0, parking: 0, receipts: [] });

export default function InvoiceForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSub = user?.role === "subcontractor";

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [existingIsSubInvoice, setExistingIsSubInvoice] = useState(false);

  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_address: "",
    issue_date: todayISO(),
    due_date: addDaysISO(30),
    payment_terms: "Net 30",
    line_items: [blankItem()],
    tax_rate: 20,
    notes: "",
    status: isSub ? "Sent" : "Draft",
    currency: "GBP",
  });

  useEffect(() => {
    SettingsAPI.get().then((s) => {
      setSettings(s);
      if (!isEdit) {
        setForm((f) => ({
          ...f,
          tax_rate: isSub ? 0 : (s.default_tax_rate ?? 20),
          payment_terms: s.default_payment_terms || "Net 30",
          currency: s.currency || "GBP",
        }));
      }
    });
  }, [isEdit, isSub]);

  useEffect(() => {
    if (isEdit) {
      InvoiceAPI.get(id)
        .then((inv) => {
          setExistingIsSubInvoice(!!inv.is_subcontractor_invoice);
          setForm({
            client_name: inv.client_name || "",
            client_email: inv.client_email || "",
            client_address: inv.client_address || "",
            issue_date: inv.issue_date,
            due_date: inv.due_date,
            payment_terms: inv.payment_terms || "Net 30",
            line_items: inv.line_items?.length
              ? inv.line_items.map((li) => ({
                  description: li.description || "",
                  quantity: li.quantity ?? 0,
                  unit_price: li.unit_price ?? 0,
                  miles: li.miles ?? 0,
                  parking: li.parking ?? 0,
                  receipts: li.receipts || [],
                }))
              : [blankItem()],
            tax_rate: inv.tax_rate ?? 20,
            notes: inv.notes || "",
            status: inv.status || "Draft",
            currency: inv.currency || "GBP",
          });
        })
        .catch(() => toast.error("Failed to load invoice"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  // Treat form as timesheet-style when: current user is a sub, OR editing an existing sub invoice
  const isTimesheet = isSub || existingIsSubInvoice;

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const updateLine = (idx, field, value) => {
    setForm((f) => {
      const items = [...f.line_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, line_items: items };
    });
  };

  const addLine = () =>
    setForm((f) => ({ ...f, line_items: [...f.line_items, blankItem()] }));

  const removeLine = (idx) =>
    setForm((f) => ({
      ...f,
      line_items: f.line_items.filter((_, i) => i !== idx),
    }));

  const subtotal = form.line_items.reduce(
    (s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0),
    0
  );
  const mileageRate = Number(settings?.mileage_rate || 0);
  const freeMiles = Number(settings?.mileage_free_miles_per_day ?? 100);
  const lineMileage = (li) => {
    const miles = Math.max(0, Number(li.miles || 0));
    const billable = Math.max(0, miles - freeMiles);
    return billable * mileageRate;
  };
  const mileageTotal = isTimesheet
    ? form.line_items.reduce((s, li) => s + lineMileage(li), 0)
    : 0;
  const parkingTotal = isTimesheet
    ? form.line_items.reduce((s, li) => s + Math.max(0, Number(li.parking || 0)), 0)
    : 0;
  const effectiveTaxRate = isTimesheet ? 0 : Number(form.tax_rate || 0);
  const taxableBase = subtotal + mileageTotal + parkingTotal;
  const taxAmount = taxableBase * (effectiveTaxRate / 100);
  const total = taxableBase + taxAmount;

  const handleSave = async () => {
    if (!isTimesheet && !form.client_name.trim()) {
      toast.error("Client name is required");
      return;
    }
    if (!form.line_items.length) {
      toast.error("Add at least one line item");
      return;
    }
    // Enforce: parking > 0 requires receipt(s)
    for (let i = 0; i < form.line_items.length; i++) {
      const li = form.line_items[i];
      if (Number(li.parking || 0) > 0 && !(li.receipts && li.receipts.length)) {
        toast.error(`Row ${i + 1}: please attach a receipt to claim parking`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        tax_rate: isTimesheet ? 0 : Number(form.tax_rate),
        line_items: form.line_items.map((li) => ({
          description: li.description,
          quantity: Number(li.quantity),
          unit_price: Number(li.unit_price),
          miles: Number(li.miles || 0),
          parking: Number(li.parking || 0),
          receipts: (li.receipts || []).map((r) => ({
            id: r.id,
            storage_path: r.storage_path,
            original_filename: r.original_filename || "",
            content_type: r.content_type || "application/octet-stream",
            size: r.size || 0,
          })),
        })),
      };
      const result = isEdit
        ? await InvoiceAPI.update(id, payload)
        : await InvoiceAPI.create(payload);
      toast.success(isEdit ? "Invoice updated" : "Invoice created");
      navigate(`/invoices/${result.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save invoice";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const uploadReceipts = async (idx, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      const uploaded = [];
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name}: too large (max 10 MB)`);
          continue;
        }
        const ref = await ReceiptAPI.upload(f);
        uploaded.push(ref);
      }
      if (uploaded.length) {
        setForm((f) => {
          const items = [...f.line_items];
          const existing = items[idx].receipts || [];
          items[idx] = { ...items[idx], receipts: [...existing, ...uploaded] };
          return { ...f, line_items: items };
        });
        toast.success(`${uploaded.length} receipt${uploaded.length === 1 ? "" : "s"} uploaded`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to upload receipt";
      toast.error(msg);
    }
  };

  const removeReceipt = (idx, receiptId) => {
    setForm((f) => {
      const items = [...f.line_items];
      items[idx] = {
        ...items[idx],
        receipts: (items[idx].receipts || []).filter((r) => r.id !== receiptId),
      };
      return { ...f, line_items: items };
    });
  };

  if (loading) {
    return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  }

  const descLabel = isTimesheet ? "Day / Description" : "Description";
  const qtyLabel = isTimesheet ? "Hours" : "Qty";
  const priceLabel = isTimesheet ? "Rate" : "Unit Price";
  const descPlaceholder = isTimesheet
    ? "e.g. Mon 03/02 — on-site Cardiff"
    : "e.g., Server maintenance — 8 hours";

  return (
    <div data-testid="invoice-form-page">
      <PageHeader
        overline={isEdit ? "Edit" : "Create"}
        title={isEdit ? "Edit Invoice" : isTimesheet ? "New Timesheet Invoice" : "New Invoice"}
        description={
          isTimesheet
            ? "Submit the days/hours you worked and your rate — no client info or VAT required."
            : "Fill in client details and itemize the work performed."
        }
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => navigate(-1)}
              data-testid="cancel-invoice-btn"
            >
              <ArrowLeft size={16} weight="bold" className="mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-sm"
              data-testid="save-invoice-btn"
            >
              <FloppyDisk size={16} weight="bold" className="mr-2" />
              {saving
                ? "Saving…"
                : isEdit
                  ? "Update Invoice"
                  : isTimesheet
                    ? "Submit to HEO"
                    : "Save Invoice"}
            </Button>
          </div>
        }
      />

      {/* Top row: Client info (non-sub only) + Dates */}
      <div className={`grid grid-cols-1 ${isTimesheet ? "" : "lg:grid-cols-3"} gap-px bg-border border border-border`}>
        {!isTimesheet && (
          <section className="lg:col-span-2 bg-white p-6 lg:p-8">
            <div className="overline text-primary mb-4">Bill To</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="client_name" className="overline">Client Name *</Label>
                <Input
                  id="client_name"
                  value={form.client_name}
                  onChange={(e) => update("client_name", e.target.value)}
                  placeholder="Acme Ltd."
                  className="rounded-sm mt-1.5"
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <Label htmlFor="client_email" className="overline">Client Email</Label>
                <Input
                  id="client_email"
                  type="email"
                  value={form.client_email}
                  onChange={(e) => update("client_email", e.target.value)}
                  placeholder="billing@acme.com"
                  className="rounded-sm mt-1.5"
                  data-testid="client-email-input"
                />
              </div>
              <div>
                <Label htmlFor="status" className="overline">Status</Label>
                <Select value={form.status} onValueChange={(v) => update("status", v)}>
                  <SelectTrigger id="status" className="rounded-sm mt-1.5" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="client_address" className="overline">Client Address</Label>
                <Textarea
                  id="client_address"
                  value={form.client_address}
                  onChange={(e) => update("client_address", e.target.value)}
                  rows={3}
                  placeholder="Street, City, Postcode"
                  className="rounded-sm mt-1.5 resize-none"
                  data-testid="client-address-input"
                />
              </div>
            </div>
          </section>
        )}

        <section className={`bg-white p-6 lg:p-8 ${isTimesheet ? "lg:col-span-1" : ""}`}>
          <div className="overline text-primary mb-4">Schedule</div>
          <div className={`${isTimesheet ? "grid grid-cols-1 sm:grid-cols-2 gap-4" : "space-y-4"}`}>
            <div>
              <Label htmlFor="issue_date" className="overline">Issue Date</Label>
              <Input
                id="issue_date"
                type="date"
                value={form.issue_date}
                onChange={(e) => update("issue_date", e.target.value)}
                className="rounded-sm mt-1.5"
                data-testid="issue-date-input"
              />
            </div>
            <div>
              <Label htmlFor="due_date" className="overline">Due Date</Label>
              <Input
                id="due_date"
                type="date"
                value={form.due_date}
                onChange={(e) => update("due_date", e.target.value)}
                className="rounded-sm mt-1.5"
                data-testid="due-date-input"
              />
            </div>
            <div>
              <Label htmlFor="payment_terms" className="overline">Payment Terms</Label>
              <Input
                id="payment_terms"
                value={form.payment_terms}
                onChange={(e) => update("payment_terms", e.target.value)}
                placeholder="Net 30"
                className="rounded-sm mt-1.5"
                data-testid="payment-terms-input"
              />
            </div>
            {isTimesheet && (
              <div>
                <Label htmlFor="status_sub" className="overline">Status</Label>
                <Select value={form.status} onValueChange={(v) => update("status", v)}>
                  <SelectTrigger id="status_sub" className="rounded-sm mt-1.5" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Line Items */}
      <section className="border-x border-b border-border bg-white p-6 lg:p-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="overline text-primary">
              {isTimesheet ? "Timesheet" : "Line Items"}
            </div>
            <h2 className="font-display text-xl font-bold tracking-tight mt-1">
              {isTimesheet ? "Days & Hours Worked" : "Services Rendered"}
            </h2>
          </div>
          <Button variant="outline" onClick={addLine} className="rounded-sm" data-testid="add-line-item-btn">
            <Plus size={16} weight="bold" className="mr-2" />
            {isTimesheet ? "Add Day" : "Add Line"}
          </Button>
        </div>

        {isTimesheet && (
          <div className="mb-4 p-3 bg-muted/40 border border-border text-xs text-muted-foreground space-y-1">
            <div>
              <span className="font-semibold text-foreground">Mileage policy:</span>{" "}
              Rate <span className="font-mono font-semibold">{formatCurrency(mileageRate, form.currency)}</span>/mile.
              Only miles above the first <span className="font-mono font-semibold">{freeMiles}</span>/day are billable.
              {mileageRate === 0 && (
                <span className="block mt-1 text-amber-700">Note: the admin has not set a mileage rate yet — mileage will total {formatCurrency(0, form.currency)}.</span>
              )}
            </div>
            <div>
              <span className="font-semibold text-foreground">Parking policy:</span>{" "}
              Enter the amount paid for parking per day. You <span className="font-semibold text-foreground">must attach a receipt</span> to claim parking money.
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-3 overline text-muted-foreground font-bold">
                  {descLabel}
                </th>
                <th className="text-right py-2 px-3 overline text-muted-foreground font-bold w-20">
                  {qtyLabel}
                </th>
                <th className="text-right py-2 px-3 overline text-muted-foreground font-bold w-24">
                  {priceLabel}
                </th>
                {isTimesheet && (
                  <>
                    <th className="text-right py-2 px-3 overline text-muted-foreground font-bold w-20">
                      Miles
                    </th>
                    <th className="text-right py-2 px-3 overline text-muted-foreground font-bold w-24">
                      Mileage £
                    </th>
                    <th className="text-right py-2 px-3 overline text-muted-foreground font-bold w-24">
                      Parking £
                    </th>
                  </>
                )}
                <th className="text-right py-2 px-3 overline text-muted-foreground font-bold w-28">
                  Line Total
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {form.line_items.map((li, idx) => {
                const workAmount = Number(li.quantity || 0) * Number(li.unit_price || 0);
                const mileAmount = isTimesheet ? lineMileage(li) : 0;
                const parkingAmount = isTimesheet ? Math.max(0, Number(li.parking || 0)) : 0;
                const lineTotal = workAmount + mileAmount + parkingAmount;
                const needsReceipt = parkingAmount > 0 && !(li.receipts && li.receipts.length);
                return (
                  <Fragment key={idx}>
                    <tr
                      key={`r-${idx}`}
                      className="border-b border-border/60 fade-in-up"
                      style={{ animationDelay: `${idx * 40}ms` }}
                      data-testid={`line-item-row-${idx}`}
                    >
                      <td className="py-2 pr-3">
                        <Input
                          value={li.description}
                          onChange={(e) => updateLine(idx, "description", e.target.value)}
                          placeholder={descPlaceholder}
                          className="rounded-sm border-transparent hover:border-input focus:border-input"
                          data-testid={`line-item-desc-${idx}`}
                        />
                      </td>
                      <td className="py-2 px-3">
                        <Input
                          type="number"
                          step="0.01"
                          value={li.quantity}
                          onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                          className="rounded-sm text-right font-mono"
                          data-testid={`line-item-qty-${idx}`}
                        />
                      </td>
                      <td className="py-2 px-3">
                        <Input
                          type="number"
                          step="0.01"
                          value={li.unit_price}
                          onChange={(e) => updateLine(idx, "unit_price", e.target.value)}
                          className="rounded-sm text-right font-mono"
                          data-testid={`line-item-price-${idx}`}
                        />
                      </td>
                      {isTimesheet && (
                        <>
                          <td className="py-2 px-3">
                            <Input
                              type="number"
                              step="0.1"
                              min="0"
                              value={li.miles ?? 0}
                              onChange={(e) => updateLine(idx, "miles", e.target.value)}
                              placeholder="0"
                              className="rounded-sm text-right font-mono"
                              data-testid={`line-item-miles-${idx}`}
                            />
                          </td>
                          <td className="py-2 px-3 text-right font-mono text-muted-foreground" data-testid={`line-item-mileage-amount-${idx}`}>
                            {formatCurrency(mileAmount, form.currency)}
                          </td>
                          <td className="py-2 px-3">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={li.parking ?? 0}
                              onChange={(e) => updateLine(idx, "parking", e.target.value)}
                              placeholder="0.00"
                              className={`rounded-sm text-right font-mono ${needsReceipt ? "border-destructive" : ""}`}
                              data-testid={`line-item-parking-${idx}`}
                            />
                          </td>
                        </>
                      )}
                      <td className="py-2 px-3 text-right font-mono font-semibold">
                        {formatCurrency(lineTotal, form.currency)}
                      </td>
                      <td className="py-2 pl-2">
                        <button
                          onClick={() => removeLine(idx)}
                          disabled={form.line_items.length === 1}
                          className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`remove-line-item-${idx}`}
                          aria-label="Remove line"
                        >
                          <Trash size={16} weight="bold" />
                        </button>
                      </td>
                    </tr>
                    {isTimesheet && (
                      <tr key={`a-${idx}`} className="border-b border-border/60 bg-muted/20">
                        <td colSpan={8} className="py-2 pr-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <label
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-border rounded-sm cursor-pointer hover:bg-background"
                              data-testid={`attach-receipt-${idx}`}
                            >
                              <Paperclip size={14} weight="bold" />
                              Attach parking receipt
                              <input
                                type="file"
                                multiple
                                accept="image/*,application/pdf"
                                className="hidden"
                                onChange={(e) => {
                                  uploadReceipts(idx, e.target.files);
                                  e.target.value = "";
                                }}
                              />
                            </label>
                            {needsReceipt && (
                              <span className="text-xs text-destructive font-semibold" data-testid={`parking-needs-receipt-${idx}`}>
                                Receipt required to claim parking
                              </span>
                            )}
                            {(li.receipts || []).map((r) => {
                              const isPdf = (r.content_type || "").includes("pdf");
                              return (
                                <span
                                  key={r.id}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-background border border-border rounded-sm"
                                  data-testid={`receipt-chip-${idx}-${r.id}`}
                                >
                                  {isPdf ? <FilePdf size={14} weight="bold" /> : <ImageIcon size={14} weight="bold" />}
                                  <span className="max-w-[160px] truncate">{r.original_filename || "receipt"}</span>
                                  <button
                                    onClick={() => removeReceipt(idx, r.id)}
                                    className="ml-1 opacity-60 hover:opacity-100 hover:text-destructive"
                                    aria-label="Remove receipt"
                                    data-testid={`remove-receipt-${idx}-${r.id}`}
                                  >
                                    <X size={12} weight="bold" />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Totals + Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border border-x border-b border-border">
        <section className="lg:col-span-2 bg-white p-6 lg:p-8">
          <Label htmlFor="notes" className="overline">Notes</Label>
          <Textarea
            id="notes"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={5}
            placeholder={isTimesheet ? "Any extra notes (e.g. travel, overtime)…" : "Thanks for your business. Please reference invoice number on payment."}
            className="rounded-sm mt-1.5 resize-none"
            data-testid="notes-input"
          />
        </section>

        <section className="bg-white p-6 lg:p-8">
          <div className="overline text-primary mb-4">Totals</div>
          <div className="space-y-3">
            {isTimesheet ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Hours Subtotal</span>
                  <span className="font-mono font-semibold" data-testid="form-hours-subtotal">
                    {formatCurrency(subtotal, form.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Mileage Subtotal</span>
                  <span className="font-mono font-semibold" data-testid="form-mileage-subtotal">
                    {formatCurrency(mileageTotal, form.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Parking Subtotal</span>
                  <span className="font-mono font-semibold" data-testid="form-parking-subtotal">
                    {formatCurrency(parkingTotal, form.currency)}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono font-semibold" data-testid="form-subtotal">
                    {formatCurrency(subtotal, form.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="tax_rate" className="text-sm text-muted-foreground shrink-0">
                    VAT %
                  </Label>
                  <Input
                    id="tax_rate"
                    type="number"
                    step="0.1"
                    value={form.tax_rate}
                    onChange={(e) => update("tax_rate", e.target.value)}
                    className="rounded-sm w-20 text-right font-mono"
                    data-testid="tax-rate-input"
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">VAT Amount</span>
                  <span className="font-mono font-semibold" data-testid="form-tax-amount">
                    {formatCurrency(taxAmount, form.currency)}
                  </span>
                </div>
              </>
            )}
            <div className="border-t border-border pt-3 flex items-center justify-between">
              <span className="overline text-primary">Total</span>
              <span
                className="font-display font-black text-2xl tracking-tighter"
                data-testid="form-total"
              >
                {formatCurrency(total, form.currency)}
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
